import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { toast } from "react-toastify";
import api from "../../../../conf/api";
import { WcaBadge } from "./constants";

/**
 * Traceability: given a WGID, which compilations was it built into?
 *
 * Collapsed by default — it's an occasional lookup, and left expanded it
 * pushed the actual compilation grid below the fold on every visit.
 */
export default function WgidLookupBox() {
  const [open, setOpen] = useState(false);
  const [wgid, setWgid] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    const q = wgid.trim();
    if (!q) return;
    setLoading(true);
    try {
      const r = await api.get("/mrss/compilations/by-wgid/", { params: { wgid: q } });
      setResults(r.data.compilations || []);
    } catch {
      toast.error("Lookup failed");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: "1px solid #e6ebf1", overflow: "hidden" }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 1.25 },
          minHeight: 48, cursor: "pointer",
          "&:hover": { bgcolor: "#f8fafc" },
        }}
      >
        <TravelExploreIcon sx={{ fontSize: 18, color: "#0f766e", flexShrink: 0 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
          Trace a WGID
        </Typography>
        {/* The strapline is the first thing to go on a phone — it would
            otherwise squeeze the heading it explains down to an ellipsis. */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mr: 0.5, display: { xs: "none", md: "block" } }}
        >
          Find every compilation a source video was used in
        </Typography>
        <IconButton size="small" sx={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, pt: 0.5, borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, mt: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Paste a WGID…"
              value={wgid}
              onChange={(e) => setWgid(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafc" } }}
            />
            <Button
              variant="contained"
              disableElevation
              onClick={handleSearch}
              disabled={loading || !wgid.trim()}
              sx={{ borderRadius: 2, textTransform: "none", minWidth: 96, minHeight: 40, flexShrink: 0 }}
            >
              {loading ? <CircularProgress size={18} color="inherit" /> : "Search"}
            </Button>
          </Box>

          {results !== null && (
            <Fade in>
              <Box mt={1.5}>
                {results.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Not attached to any compilation yet.
                  </Typography>
                ) : (
                  <Box display="flex" flexDirection="column" gap={0.75}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Used in {results.length} compilation{results.length !== 1 ? "s" : ""}
                    </Typography>
                    {results.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          p: 1, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #eef2f6",
                          display: "flex", alignItems: "center", gap: 1,
                          flexWrap: { xs: "wrap", sm: "nowrap" }, minWidth: 0,
                        }}
                      >
                        <WcaBadge size="small">{c.wca_id}</WcaBadge>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ flex: { xs: "1 1 100%", sm: 1 }, order: { xs: 3, sm: 0 }, minWidth: 0 }}
                        >
                          {c.title}
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<BusinessIcon sx={{ fontSize: 13 }} />}
                          label={c.brand || "no brand"}
                          sx={{ height: 21, fontSize: 11, maxWidth: "100%", flexShrink: 0 }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
